<template>
	<section class="front-crud">
		<div class="pm-toolbar">
			<div class="pm-toolbar__filters">
				<el-input
					v-model="search"
					class="pm-search"
					clearable
					prefix-icon="el-icon-search"
					:placeholder="cfg.searchPlaceholder || '搜索'"
					@keyup.enter.native="searchList"
					@clear="searchList" />
			</div>
			<div class="pm-toolbar__actions">
				<el-button v-if="canAdd && !cfg.listOnly && !cfg.noCreate" type="primary" icon="el-icon-plus" class="pm-btn-primary" @click="openForm()">添加</el-button>
				<el-button
					v-if="canDel"
					type="danger"
					plain
					icon="el-icon-delete"
					:disabled="!selectedIds.length"
					@click="batchDelete">批量删除</el-button>
			</div>
		</div>

		<el-table
			v-loading="loading"
			:data="list"
			stripe
			class="pm-table"
			style="width: 100%"
			cell-class-name="pm-cell"
			header-cell-class-name="pm-header-cell"
			@selection-change="onSelectionChange">
			<el-table-column v-if="canDel" type="selection" width="48" />
			<el-table-column
				v-for="col in cfg.columns"
				:key="col.prop"
				:align="col.align || (col.width && col.width <= 90 ? 'center' : 'left')"
				:width="col.width"
				:min-width="col.minWidth"
				:label="col.label"
				:show-overflow-tooltip="true">
				<template slot-scope="scope">
					<span v-if="col.format === 'time'" class="pm-time">{{ formatTime(readCell(scope.row, col)) }}</span>
					<span v-else-if="col.format === 'money'">{{ formatMoney(readCell(scope.row, col)) }}</span>
					<span v-else-if="col.format === 'bool'">{{ truthy(readCell(scope.row, col)) ? '是' : '否' }}</span>
					<span v-else>{{ displayCell(scope.row, col) }}</span>
				</template>
			</el-table-column>
			<el-table-column align="center" width="96" label="操作" fixed="right">
				<template slot-scope="scope">
					<div v-if="canEdit || canDel" class="row-actions">
						<el-tooltip v-if="canEdit && !cfg.listOnly" content="编辑" placement="top">
							<el-button type="text" icon="el-icon-edit" class="row-action-btn" @click="openForm(scope.row)" />
						</el-tooltip>
						<el-popconfirm v-if="canDel" @onConfirm="delRow(scope.row.id)"
							confirmButtonType="danger" confirmButtonText="删除" title="确定删除该记录吗？">
							<el-button slot="reference" type="text" icon="el-icon-delete" class="row-action-btn row-action-btn--danger" />
						</el-popconfirm>
					</div>
					<span v-else>—</span>
				</template>
			</el-table-column>
		</el-table>

		<el-pagination
			class="pm-pagination"
			:hide-on-single-page="pageSingle"
			@current-change="changePage"
			@size-change="changePageSize"
			:current-page="page"
			:page-size="limit"
			:page-sizes="[10, 20, 50]"
			background
			layout="total, sizes, prev, pager, next"
			:total="listTotal" />

		<el-dialog
			:title="form.id ? '编辑' : '添加'"
			:visible.sync="formVisible"
			width="640px"
			append-to-body
			@close="resetForm">
			<el-form ref="crudForm" :model="form" :rules="formRules" label-width="96px">
				<el-form-item
					v-for="field in cfg.fields"
					:key="field.key"
					:label="field.label"
					:prop="field.required ? field.key : undefined">
					<el-input
						v-if="field.type === 'input' || field.type === 'password'"
						v-model="form[field.key]"
						:type="field.type === 'password' ? 'password' : 'text'"
						:disabled="!!field.disabled"
						:placeholder="field.placeholder || ''" />
					<el-input
						v-else-if="field.type === 'textarea'"
						v-model="form[field.key]"
						type="textarea"
						:rows="3" />
					<el-input-number
						v-else-if="field.type === 'number'"
						v-model="form[field.key]"
						:min="0"
						style="width: 100%" />
					<el-switch v-else-if="field.type === 'switch'" v-model="form[field.key]" />
					<el-select
						v-else-if="field.type === 'select'"
						v-model="form[field.key]"
						style="width: 100%">
						<el-option
							v-for="opt in fieldOptions(field)"
							:key="String(opt.value)"
							:label="opt.label"
							:value="opt.value" />
					</el-select>
					<el-select
						v-else-if="field.type === 'multiSelect'"
						v-model="form[field.key]"
						multiple
						style="width: 100%">
						<el-option
							v-for="opt in fieldOptions(field)"
							:key="String(opt.value)"
							:label="opt.label"
							:value="opt.value" />
					</el-select>
					<el-select
						v-else-if="field.type === 'parentSelect'"
						v-model="form[field.key]"
						style="width: 100%">
						<el-option :value="0" label="顶级" />
						<el-option
							v-for="opt in parentOptions"
							:key="opt.id"
							:label="opt.label || opt.name"
							:value="opt.id" />
					</el-select>
					<front-rule-tree
						v-else-if="field.type === 'ruleTree'"
						:nodes="ruleNodes"
						:value="form[field.key]"
						@input="form[field.key] = $event" />
				</el-form-item>
			</el-form>
			<div slot="footer">
				<el-button @click="formVisible = false">取消</el-button>
				<el-button type="primary" :loading="saving" @click="saveForm">保存</el-button>
			</div>
		</el-dialog>
	</section>
</template>

<script>
import FrontRuleTree from '@/components/FrontRuleTree/index.vue';
import { FRONT_ADMIN_RESOURCES } from '@/utils/front-admin-catalog';

export default {
	name: 'FrontCrud',
	components: { FrontRuleTree },
	props: {
		resource: { type: String, required: true },
		canAdd: { type: Boolean, default: false },
		canEdit: { type: Boolean, default: false },
		canDel: { type: Boolean, default: false },
	},
	data() {
		return {
			loading: false,
			saving: false,
			search: '',
			page: 1,
			limit: 10,
			listTotal: 0,
			pageSingle: false,
			list: [],
			selectedIds: [],
			formVisible: false,
			form: {},
			parentOptions: [],
			metaOptions: {},
			ruleNodes: [],
		};
	},
	computed: {
		cfg() {
			return FRONT_ADMIN_RESOURCES[this.resource] || { columns: [], fields: [], defaultForm: {} };
		},
		formRules() {
			const rules = {};
			(this.cfg.fields || []).forEach((f) => {
				if (f.required) rules[f.key] = [{ required: true, message: '请填写' + f.label, trigger: 'blur' }];
			});
			return rules;
		},
	},
	watch: {
		resource() {
			this.page = 1;
			this.search = '';
			this.loadMeta();
			this.getList();
		},
	},
	created() {
		this.resetForm();
		this.loadMeta();
		this.getList();
	},
	methods: {
		formatTime(ts) {
			const n = Number(ts);
			if (!n) return '—';
			const d = new Date(n * 1000);
			const pad = (v) => String(v).padStart(2, '0');
			return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()) + ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes());
		},
		formatMoney(v) {
			const n = Number(v);
			return Number.isFinite(n) ? n.toFixed(2) : '—';
		},
		truthy(v) {
			return v === true || v === 1 || v === '1';
		},
		readCell(row, col) {
			if (col.nested) {
				return col.nested.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), row);
			}
			return row[col.prop];
		},
		displayCell(row, col) {
			const v = this.readCell(row, col);
			if (v == null || v === '') return '—';
			return v;
		},
		fieldOptions(field) {
			if (field.options) return field.options;
			if (field.optionsAction) return this.metaOptions[field.optionsAction] || [];
			return [];
		},
		api(action, params) {
			return this.$fetch('addons/hlm/api.FrontAdmin/' + action, Object.assign({ resource: this.resource }, params || {}));
		},
		post(action, params) {
			return this.$post('addons/hlm/api.FrontAdmin/' + action, Object.assign({ resource: this.resource }, params || {}));
		},
		loadMeta() {
			const that = this;
			if (this.cfg.parentOptions) {
				this.api('manageMeta', { action: 'parentOptions' }).then(function(res) {
					if (res.code === 1) that.parentOptions = res.data || [];
				});
			}
			(this.cfg.fields || []).forEach(function(field) {
				if (field.optionsAction) {
					that.api('manageMeta', { action: field.optionsAction }).then(function(res) {
						if (res.code === 1) {
							that.$set(that.metaOptions, field.optionsAction, (res.data || []).map(function(item) {
								return { value: item.id, label: item.name || item.label || String(item.id) };
							}));
						}
					});
				}
			});
			if (this.cfg.ruleTree) {
				this.api('manageMeta', { action: this.cfg.ruleTree }).then(function(res) {
					if (res.code === 1) that.ruleNodes = res.data || [];
				});
			}
		},
		getList() {
			const that = this;
			that.loading = true;
			that.api('manageList', { page: that.page, limit: that.limit, search: that.search }).then(function(res) {
				if (res.code === 1) {
					that.list = res.data.list || [];
					that.listTotal = res.data.total || 0;
					that.pageSingle = that.listTotal <= that.limit;
				}
			}).finally(function() {
				that.loading = false;
			});
		},
		searchList() {
			this.page = 1;
			this.getList();
		},
		changePage(e) {
			this.page = e;
			this.getList();
		},
		changePageSize(size) {
			this.limit = size;
			this.page = 1;
			this.getList();
		},
		onSelectionChange(rows) {
			this.selectedIds = rows.map((r) => r.id).filter(Boolean);
		},
		resetForm() {
			this.form = Object.assign({ id: 0 }, JSON.parse(JSON.stringify(this.cfg.defaultForm || {})));
		},
		openForm(row) {
			const that = this;
			if (row && row.id && !this.cfg.listOnly) {
				this.api('manageDetail', { id: row.id }).then(function(res) {
					if (res.code === 1) {
						that.form = Object.assign({ id: row.id }, JSON.parse(JSON.stringify(that.cfg.defaultForm || {})), res.data || {});
						if (Array.isArray(that.form.group_ids)) {
							that.form.group_ids = that.form.group_ids.map(Number);
						}
						that.formVisible = true;
					}
				});
				return;
			}
			this.resetForm();
			this.formVisible = true;
		},
		saveForm() {
			const that = this;
			const run = function() {
				that.saving = true;
				const payload = Object.assign({}, that.form);
				if (Array.isArray(payload.group_ids)) payload.group_ids = payload.group_ids.join(',');
				that.post('manageSave', payload).then(function(res) {
					if (res.code === 1) {
						that.$message.success(res.msg || '保存成功');
						that.formVisible = false;
						that.getList();
					}
				}).finally(function() {
					that.saving = false;
				});
			};
			if (this.$refs.crudForm) {
				this.$refs.crudForm.validate(function(valid) {
					if (valid) run();
				});
			} else {
				run();
			}
		},
		delRow(id) {
			const that = this;
			that.api('manageDel', { ids: String(id) }).then(function(res) {
				if (res.code === 1) {
					that.$message.success('删除成功');
					that.getList();
				}
			});
		},
		batchDelete() {
			if (!this.selectedIds.length) return;
			const that = this;
			that.$confirm('确定删除选中的 ' + that.selectedIds.length + ' 条记录？', '提示', { type: 'warning' }).then(function() {
				that.api('manageDel', { ids: that.selectedIds.join(',') }).then(function(res) {
					if (res.code === 1) {
						that.$message.success('删除成功');
						that.getList();
					}
				});
			}).catch(function() {});
		},
	},
};
</script>

<style scoped>
.front-crud >>> .pm-toolbar {
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: 12px;
	padding: 16px 20px;
	border-bottom: 1px solid var(--app-border, #e5e7eb);
}
.front-crud >>> .pm-toolbar__filters,
.front-crud >>> .pm-toolbar__actions {
	display: flex;
	align-items: center;
	gap: 10px;
}
.front-crud >>> .pm-search { width: 260px; }
.front-crud >>> .pm-pagination {
	display: flex;
	justify-content: flex-end;
	padding: 16px 20px;
	border-top: 1px solid var(--app-border, #e5e7eb);
}
.front-crud >>> .row-actions {
	display: inline-flex;
	align-items: center;
	gap: 2px;
}
.front-crud >>> .pm-header-cell {
	background: #f9fafb !important;
	font-size: 13px;
	font-weight: 600;
	color: var(--app-text-secondary);
}
.front-crud >>> .pm-table { font-size: 13px; }
.front-crud >>> .row-action-btn { color: var(--app-primary); }
.front-crud >>> .row-action-btn--danger { color: #ef4444; }
.pm-time { font-size: 12px; color: var(--app-text-secondary); }
</style>
